import React, { createContext, useState, useEffect } from "react";
import { fetchUserDetails } from "../api/api.js";
import { useNavigate } from "react-router-dom";

const UserContext = createContext();

const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedAccessLevel = localStorage.getItem("accessLevel");
    if (storedAccessLevel) {
      setAccessLevel(parseInt(storedAccessLevel));
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchUserDetails();

        // Проверяем, что получили валидные данные пользователя
        if (!response || typeof response !== "object") {
          throw new Error("Неверный формат данных пользователя");
        }

        // Проверяем наличие обязательных полей
        if (!response.id || !response.username) {
          throw new Error("Отсутствуют обязательные данные пользователя");
        }

        console.log("Данные пользователя загружены:", {
          id: response.id,
          username: response.username,
          departments: response.departments?.length || 0,
          projects: response.projects?.length || 0,
          departmentsInfo:
            response.departments?.map((dept) => ({
              id: dept.id,
              name: dept.name,
              groups:
                dept.groups?.map((group) => ({
                  id: group.id,
                  name: group.name,
                  is_active: group.is_active,
                })) || [],
            })) || [],
          projectsInfo:
            response.projects?.map((project) => ({
              id: project.id,
              name: project.name,
            })) || [],
        });

        setUserData(response);

        // Сохраняем в localStorage для кэширования
        localStorage.setItem("userData", JSON.stringify(response));
      } catch (error) {
        console.error("Ошибка при загрузке данных пользователя:", error);
        setError(error);

        // Очищаем кэшированные данные при ошибке
        localStorage.removeItem("userData");

        // Перенаправляем на страницу входа только при критических ошибках
        if (
          error.message.includes("401") ||
          error.message.includes("Unauthorized")
        ) {
          navigate("/signin");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  if (loading) {
    return <div>Загрузка данных...</div>;
  }

  const updateUserData = (newUserData) => {
    setUserData(newUserData);
    if (newUserData) {
      localStorage.setItem("userData", JSON.stringify(newUserData));
    } else {
      localStorage.removeItem("userData");
    }
  };

  const refreshUserData = async () => {
    try {
      setLoading(true);
      const response = await fetchUserDetails();

      if (!response || typeof response !== "object") {
        throw new Error("Неверный формат данных пользователя");
      }

      setUserData(response);
      localStorage.setItem("userData", JSON.stringify(response));
    } catch (error) {
      console.error("Ошибка при обновлении данных пользователя:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData: updateUserData,
        refreshUserData,
        accessLevel,
        setAccessLevel,
        loading,
        error,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserProvider, UserContext };
